import {SalesAPIDataSource} from '../api/SalesAPIDataSource';
import type {SalesRepository} from './SalesRepository';
import type {BaseResponse} from '../../../Data/types/BaseResponse';
import type {Sale} from '../models/sale.model';
import type {SalesStatistics} from '../models/sale.model';
import type {SalesQueryParams} from '../models/sale.model';
import type {
  UpdateSaleRequestDTO,
  RecordPaymentRequestDTO
} from '../models/sale.dto';
import {
  mapAPISalesStatisticsToModel,
  mapBillingInvoiceDTOToModel,
  mapDetailedInvoiceDTOToModel
} from '../mappers/sale.mapper';

export class SalesRepositoryImpl implements SalesRepository {
  async getAllSales (page = 1, limit = 20, filters?: { search?: string; marketerName?: string; marketerId?: string; invoiceNumber?: string }): Promise<{ items: Sale[]; pagination?: any }> {
    const response = await SalesAPIDataSource.getAll(page, limit, filters);
    return {
      items: response.items.map(mapBillingInvoiceDTOToModel),
      pagination: response.pagination
    };
  }

  async getSalesWithFilters (params: SalesQueryParams): Promise<{ items: Sale[]; pagination?: any }> {
    // Note: Filtered API call implementation pending backend support
    const response = await SalesAPIDataSource.getAll(params.page, params.limit);
    return {
      items: response.items.map(mapBillingInvoiceDTOToModel),
      pagination: response.pagination
    };
  }

  async getSalesStatistics (): Promise<BaseResponse<SalesStatistics>> {
    try {
      const data = await SalesAPIDataSource.getStatistics();
      const mappedData = mapAPISalesStatisticsToModel(data);
      return {
        stat_code: 200,
        stat_message: 'تم جلب إحصائيات المبيعات بنجاح',
        data: mappedData
      };
    } catch (error) {
      return {
        stat_code: 500,
        stat_message: error instanceof Error ? error.message : 'حدث خطأ في جلب إحصائيات المبيعات',
        data: {
          total_invoices: 0,
          paid_invoices: 0,
          partially_paid_invoices: 0,
          unpaid_invoices: 0,
          total_amount: 0,
          total_paid: 0,
          total_remaining: 0,
          total_outstanding: 0,
          invoice_count: 0,
          top_customers: [],
          aging_report: {
            current: 0,
            days_1_30: 0,
            days_31_60: 0,
            days_61_90: 0,
            over_90: 0
          }
        }
      };
    }
  }

  async getSaleById (id: string): Promise<BaseResponse<Sale>> {
    try {
      const data = await SalesAPIDataSource.getDetailedInvoiceById(id);
      const mappedData = mapDetailedInvoiceDTOToModel(data);
      return {
        stat_code: 200,
        stat_message: 'تم جلب تفاصيل الفاتورة بنجاح',
        data: mappedData
      };
    } catch (error) {
      return {
        stat_code: 500,
        stat_message: error instanceof Error ? error.message : 'حدث خطأ في جلب تفاصيل الفاتورة',
        data: {} as Sale
      };
    }
  }

  async getSaleByInvoiceNumber (invoiceNumber: string): Promise<BaseResponse<Sale>> {
    try {
      const data = await SalesAPIDataSource.getByInvoiceNumber(invoiceNumber);
      const mappedData = mapBillingInvoiceDTOToModel(data);
      return {
        stat_code: 200,
        stat_message: 'تم جلب تفاصيل الفاتورة بنجاح',
        data: mappedData
      };
    } catch (error) {
      return {
        stat_code: 500,
        stat_message: error instanceof Error ? error.message : 'حدث خطأ في جلب تفاصيل الفاتورة',
        data: {} as Sale
      };
    }
  }


  async updateSale (id: string, saleData: UpdateSaleRequestDTO): Promise<BaseResponse<Sale>> {
    try {
      // Convert UpdateSaleRequestDTO to the format expected by the API
      const apiData: Partial<import('../api/SalesAPIDataSource').SaleDTO> = {
        status: saleData.status as 'delivered' | 'pending' | 'cancelled',
        notes: saleData.notes
      };
      const data = await SalesAPIDataSource.update(id, apiData);
      const mappedData = mapBillingInvoiceDTOToModel(data);
      return {
        stat_code: 200,
        stat_message: 'تم تحديث المبيعة بنجاح',
        data: mappedData
      };
    } catch (error) {
      return {
        stat_code: 500,
        stat_message: error instanceof Error ? error.message : 'حدث خطأ في تحديث المبيعة',
        data: {} as Sale
      };
    }
  }

  async deleteSale (id: string): Promise<BaseResponse<void>> {
    try {
      await SalesAPIDataSource.delete(id);
      return {
        stat_code: 200,
        stat_message: 'تم حذف المبيعة بنجاح',
        data: undefined
      };
    } catch (error) {
      return {
        stat_code: 500,
        stat_message: error instanceof Error ? error.message : 'حدث خطأ في حذف المبيعة',
        data: undefined
      };
    }
  }

  async recordPayment (paymentData: RecordPaymentRequestDTO): Promise<BaseResponse<Sale>> {
    try {
      const data = await SalesAPIDataSource.recordPayment(paymentData);
      const mappedData = mapBillingInvoiceDTOToModel(data);
      return {
        stat_code: 200,
        stat_message: 'تم تسجيل الدفعة بنجاح',
        data: mappedData
      };
    } catch (error) {
      return {
        stat_code: 500,
        stat_message: error instanceof Error ? error.message : 'حدث خطأ في تسجيل الدفعة',
        data: {} as Sale
      };
    }
  }
}
